import { FC, useCallback } from "react";
import { FormControlLabel, Tooltip } from "@mui/material";
import styled, { css } from "styled-components";
import YesNoSwitch from "components/common/ui/YesNoSwitch";
import { FoodTag } from "client/jspPlatformExperiment";

interface FoodTagSwitchProps {
  foodTag: FoodTag;
  defaultState?: boolean;
  onChange: (changeTag: FoodTag) => void;
  hints?: string;
  viewOnly?: boolean;
  /**
   * Flag a read-only toggle driven by something else (e.g. a diet/texture tag
   * implied by the food's variant type). "Yes" shows a vivid orange lock-dot on
   * an orange track; "no" dims the switch with a lock on the thumb. Either way
   * the dimmed label and lock read as locked while the ✓/✕ shows the status.
   */
  warn?: boolean;
}

// White lock glyph on the orange thumb for the read-only "yes" state.
// eslint-disable-next-line max-len
const iconLockWhite = `'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="%23ffffff" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>'`;

// Dark lock glyph on the (light) thumb for the read-only "no" state.
// eslint-disable-next-line max-len
const iconLockDark = `'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="%23555555" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>'`;

const StyledFormControlLabel = styled(FormControlLabel)<{
  $readOnlyYes?: boolean;
  $readOnlyNo?: boolean;
}>`
  padding: 0 12px 0 6px;

  /* Read-only "yes": vivid orange lock-dot + orange (dimmed) track. */
  ${({ $readOnlyYes }) =>
    $readOnlyYes &&
    css`
      & .MuiSwitch-switchBase.Mui-checked {
        color: #ed6c02;
      }

      & .MuiSwitch-thumb {
        background-image: url(${iconLockWhite});
        background-size: 14px 14px;
        background-repeat: no-repeat;
        background-position: center;
      }

      & .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
        background-color: #ed6c02;
        opacity: 0.4;
      }

      & .MuiFormControlLabel-label {
        opacity: 0.5;
      }
    `}

  /* Read-only "no": dim the whole switch with a lock on the thumb. */
  ${({ $readOnlyNo }) =>
    $readOnlyNo &&
    css`
      & .MuiSwitch-root {
        opacity: 0.4;
      }

      & .MuiSwitch-thumb {
        background-image: url(${iconLockDark});
        background-size: 14px 14px;
        background-repeat: no-repeat;
        background-position: center;
      }

      & .MuiFormControlLabel-label {
        opacity: 0.5;
      }
    `}
`;

const FoodTagSwitch: FC<FoodTagSwitchProps> = ({
  foodTag,
  onChange,
  defaultState = false,
  hints = "",
  viewOnly = false,
  warn = false,
}) => {
  // Controlled by the parent's tag list (via `defaultState`) instead of local
  // state, so the switch stays correct when the tag set changes from outside —
  // e.g. the Change Variant Type dialog adds this tag, or tags are copied to a
  // new variant. A local copy would show a stale value in those cases.
  const toggleOnOff = useCallback(() => {
    if (!viewOnly) {
      onChange(foodTag);
    }
  }, [foodTag, onChange, viewOnly]);

  return (
    <Tooltip title={hints} placement="top">
      <StyledFormControlLabel
        $readOnlyYes={warn && defaultState}
        $readOnlyNo={warn && !defaultState}
        control={<YesNoSwitch checked={defaultState} onChange={toggleOnOff} />}
        label={foodTag.tag_name}
      />
    </Tooltip>
  );
};

export default FoodTagSwitch;
